import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { assetsAPI, projectsAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

const assetFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  description: z.string().optional(),
  category: z.enum(['VEHICLE', 'PROPERTY', 'LAND', 'HOUSE', 'OTHER']),
  acquisitionValue: z.coerce.number().positive('Valor deve ser positivo'),
  marketValue: z.coerce.number().positive('Valor deve ser positivo').optional().or(z.literal(0)).or(z.literal('')),
  projectId: z.string().optional(),
  acquiredAt: z.string().optional(),
})

type AssetFormValues = z.infer<typeof assetFormSchema>

const CATEGORY_OPTIONS = [
  { value: 'VEHICLE', label: 'Veiculo' },
  { value: 'PROPERTY', label: 'Imovel' },
  { value: 'LAND', label: 'Terreno' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'OTHER', label: 'Outro' },
]

interface AssetFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset?: any
}

export function AssetFormDialog({
  open,
  onOpenChange,
  asset,
}: AssetFormDialogProps) {
  const queryClient = useQueryClient()
  const isEdit = !!asset

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: open,
  })

  const projects = projectsData?.data || []

  const form = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: asset
      ? {
          name: asset.name,
          description: asset.description || '',
          category: asset.category,
          acquisitionValue: asset.acquisitionValue,
          marketValue: asset.marketValue || '',
          projectId: asset.projectId || '',
        }
      : {
          name: '',
          description: '',
          category: 'HOUSE' as const,
          acquisitionValue: 0,
          marketValue: '',
          projectId: '',
          acquiredAt: '',
        },
  })

  const mutation = useMutation({
    mutationFn: async (values: AssetFormValues) => {
      const payload: any = {
        name: values.name,
        description: values.description || undefined,
        category: values.category,
        acquisitionValue: values.acquisitionValue,
        marketValue: values.marketValue && Number(values.marketValue) > 0 ? Number(values.marketValue) : undefined,
        projectId: values.projectId || undefined,
      }

      if (!isEdit && values.acquiredAt) {
        payload.acquiredAt = values.acquiredAt
      }

      if (isEdit) {
        return assetsAPI.update(asset.id, payload)
      }
      return assetsAPI.create(payload)
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Ativo atualizado!' : 'Ativo cadastrado!')
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ['asset', asset.id] })
      }
      onOpenChange(false)
      form.reset()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: AssetFormValues) => {
    mutation.mutate(values)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Ativo' : 'Novo Ativo'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informacoes do ativo.'
              : 'Preencha as informacoes para cadastrar um novo ativo.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder="Ex: Honda Civic 2023, Casa 05 - Cond. Aurora"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive mt-1">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Descricao</Label>
            <Textarea
              id="description"
              {...form.register('description')}
              placeholder="Detalhes do ativo..."
              rows={3}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Categoria *</Label>
              <Select
                value={form.watch('category')}
                onValueChange={(v) => form.setValue('category', v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isEdit && (
              <div>
                <Label htmlFor="acquiredAt">Data de Aquisicao</Label>
                <Input
                  id="acquiredAt"
                  type="date"
                  {...form.register('acquiredAt')}
                />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="acquisitionValue">Valor de Aquisicao *</Label>
              <Input
                id="acquisitionValue"
                type="number"
                step="0.01"
                {...form.register('acquisitionValue')}
                placeholder="0,00"
              />
              {form.formState.errors.acquisitionValue && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.acquisitionValue.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="marketValue">Valor de Mercado</Label>
              <Input
                id="marketValue"
                type="number"
                step="0.01"
                {...form.register('marketValue')}
                placeholder="0,00"
              />
            </div>
          </div>

          <div>
            <Label>Projeto</Label>
            <Select
              value={form.watch('projectId') || 'NONE'}
              onValueChange={(v) => form.setValue('projectId', v === 'NONE' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um projeto (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Nenhum</SelectItem>
                {projects.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? 'Salvar' : 'Cadastrar Ativo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
